import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { staffApi, setUser, fmtDate, fmtErr } from "../api";

const Field = ({ label, value }) => (
  <div className="border-b border-slate-100 py-3">
    <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</p>
    <p className="mt-1 text-sm font-medium text-slate-800">{value || "—"}</p>
  </div>
);

const Profile = () => {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", dob: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await staffApi.get("/auth/me");
        setMe(data);
        setForm({ name: data.name || "", phone: data.phone || "", dob: data.dob || "", password: "" });
      } catch (e) {
        setError(fmtErr(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    const payload = { name: form.name.trim(), phone: form.phone.trim() || null, dob: form.dob || null };
    if (form.password) {
      if (form.password.length < 8) {
        setFormError("New password must be at least 8 characters.");
        return;
      }
      payload.password = form.password;
    }
    setBusy(true);
    try {
      const { data } = await staffApi.patch("/users/me", payload);
      setMe(data);
      setUser(data);
      setForm((f) => ({ ...f, password: "" }));
      toast.success("Profile updated.");
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>;
  if (error) return <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="profile-error">{error}</p>;

  return (
    <div data-testid="staff-profile">
      <h1 className="font-display text-2xl font-bold text-slate-900">My Profile</h1>
      <p className="mt-1 text-sm text-slate-500">Your employee record. Only name, phone, date of birth and password can be edited here.</p>
      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 p-6 lg:p-8" data-testid="profile-details-card">
          <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 mb-2">Employee Record</p>
          <Field label="Name" value={me.name} />
          <Field label="Employee Code" value={me.employee_code} />
          <Field label="Email" value={me.email} />
          <Field label="Designation" value={me.designation} />
          <Field label="Department" value={me.department} />
          <Field label="Role" value={me.role} />
          <Field label="Grade" value={me.grade} />
          <Field label="Location" value={me.location} />
          <Field label="Date of Joining" value={fmtDate(me.date_of_joining)} />
        </div>
        <div className="bg-white border border-slate-200 p-6 lg:p-8" data-testid="profile-edit-card">
          <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Edit Details</p>
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <label htmlFor="pf-name" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Name</label>
              <input id="pf-name" type="text" required data-testid="profile-name-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="pf-phone" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Phone</label>
              <input id="pf-phone" type="tel" data-testid="profile-phone-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label htmlFor="pf-dob" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Date of Birth</label>
              <input id="pf-dob" type="date" data-testid="profile-dob-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]"
                value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
            </div>
            <div>
              <label htmlFor="pf-pass" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">New Password (optional)</label>
              <input id="pf-pass" type="password" autoComplete="new-password" data-testid="profile-password-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <p className="mt-1 text-[11px] text-slate-400">Leave blank to keep your current password.</p>
            </div>
            {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="profile-form-error">{formError}</p>}
            <button type="submit" disabled={busy} data-testid="profile-save-btn"
              className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors duration-150 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} Save Changes
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
